// @flow
import { connect } from 'react-redux'
import Form, { type Props } from './Form'
import type { Store } from '../../redux/ducks'
import { patch, reset } from '../../redux/ducks/leadformEdit/reducers'
import type { LeadformErrors, State } from '../../redux/ducks/leadformEdit/reducers'
import store from '../../redux/store'
import postJson from '../../services/postJson'
import { getMedia } from '../../redux/selectors'
import _ from 'lodash'
import { init as initLeadforms } from '../../redux/ducks/leadforms/reducers'
import type {
  Configs,
  Leadform,
  LeadformCustomQuestion,
  LeadformQuestion,
  LeadformRequest,
  LeadformResponse,
  LeadformStockQuestions,
  Media,
  PersistedMedia
} from '../types'
import { singleProviderOutputItem } from '../prepareProviderTemplateBlocks'
import { singleCrop } from '../../redux/ducks/AdvertisementDataAdapter'
import type { SocialPublicProps } from '../../redux/ducks/audienceEdit/reducers'

function mapStateToProps(state: Store, ownProps: OwnProps) {
  const persistedImage: ?PersistedMedia = state.leadformEdit.imageStoreId
    ? getMedia(state, state.leadformEdit.imageStoreId)
    : null
  const image: ?Media = persistedImage
    ? {
        src: persistedImage.src,
        name: persistedImage.name,
        width: persistedImage.width,
        height: persistedImage.height,
        size: persistedImage.size
      }
    : null
  const {
    id,
    name,
    vkGroup,
    fbGroup,
    title,
    description,
    imageStoreId,
    vkCrop,
    privacyPolicyUrl,
    confirmationText,
    nameErrors,
    vkGroupErrors,
    fbGroupErrors,
    titleErrors,
    descriptionErrors,
    imageErrors,
    questionsErrors,
    privacyPolicyUrlErrors,
    confirmationTextErrors,
    isOpen,
    isLoading
  } = state.leadformEdit

  const { configs, isOrtAccounts, adAccountId, socialPublicProps } = ownProps

  return {
    id,
    configs,
    isOrtAccounts,
    adAccountId,
    name,
    vkGroup,
    fbGroup,
    title,
    description,
    imageStoreId,
    vkCrop,
    privacyPolicyUrl,
    confirmationText,
    nameErrors,
    vkGroupErrors,
    fbGroupErrors,
    titleErrors,
    descriptionErrors,
    imageErrors,
    questionsErrors,
    privacyPolicyUrlErrors,
    confirmationTextErrors,
    socialPublicProps,
    isOpen,
    isLoading,
    image
  }
}

type OwnProps = {|
  socialPublicProps: SocialPublicProps,
  isOrtAccounts: boolean,
  adAccountId: number,
  configs: Configs
|}

function mapDispatchToProps(dispatch: Function) {
  return {
    onClose: () => {
      dispatch(patch({ isOpen: false }))
    },
    onChange: (diff: $Shape<State>) => {
      dispatch(patch(diff))
    },
    onPersist: () => {
      const state = store.getState()
      const {
        id,
        name,
        vkGroup,
        fbGroup,
        title,
        description,
        imageStoreId,
        vkCrop,
        stockQuestions,
        questions,
        privacyPolicyUrl,
        confirmationText
      } = state.leadformEdit
      const image = getMedia(state, imageStoreId)
      const imageId = image && image.id
      const url = id ? `/api/v1/leadforms/${id}` : '/api/v1/leadforms'
      const request: LeadformRequest = {
        name,
        vk_public_id: vkGroup && vkGroup.id,
        fb_public_id: fbGroup && fbGroup.id,
        title,
        description,
        image_id: imageId,
        vk_crop: vkCrop && singleProviderOutputItem(vkCrop),
        questions: formatQuestions(stockQuestions, questions),
        privacy_policy_url: privacyPolicyUrl,
        confirmation_text: confirmationText
      }
      dispatch(patch({ isLoading: true }))
      postJson(url, {
        data: request
      })
        .done((response) => {
          const leadformResponse: LeadformResponse = response.data
          const state = store.getState()
          const leadforms = _.cloneDeep(state.leadforms.items)
          const leadform: Leadform = {
            id: leadformResponse.id,
            name: leadformResponse.name,
            vkGroup: leadformResponse.vk_public,
            fbGroup: leadformResponse.fb_public,
            title: leadformResponse.title,
            description: leadformResponse.description,
            imageStoreId,
            vkCrop: leadformResponse.vk_crop && singleCrop(leadformResponse.vk_crop),
            stockQuestions: getStockQuestions(leadformResponse.questions),
            questions: getCustomQuestions(leadformResponse.questions),
            privacyPolicyUrl: leadformResponse.privacy_policy_url,
            confirmationText: leadformResponse.confirmation_text
          }
          if (id === null) {
            leadforms.push(leadform)
          } else {
            const index = _.findIndex(leadforms, { id: id })
            leadforms[index] = leadform
          }
          dispatch(initLeadforms({ items: leadforms }))
          dispatch(reset())
        })
        .fail((e: any) => {
          if (e.status === 422) {
            dispatch(patch(processErrors(e.responseJSON.message)))
          } else {
            window.alert('Request error!')
          }
        })
        .always(() => {
          dispatch(patch({ isLoading: false }))
        })
    }
  }
}

export function getStockQuestions(questions: Array<LeadformQuestion>): LeadformStockQuestions {
  return {
    name: _.some(questions, (q) => q.type === 'first_name'),
    patronymic: _.some(questions, (q) => q.type === 'middle_name'),
    surname: _.some(questions, (q) => q.type === 'last_name'),
    email: _.some(questions, (q) => q.type === 'email'),
    phone: _.some(questions, (q) => q.type === 'phone_number'),
    age: _.some(questions, (q) => q.type === 'age'),
    birthday: _.some(questions, (q) => q.type === 'birthday'),
    location: _.some(questions, (q) => q.type === 'location')
  }
}

export function getCustomQuestions(
  questions: Array<LeadformQuestion>
): Array<LeadformCustomQuestion> {
  return _.compact(_.map(questions, (q) => getCustomQuestion(q)))
}

function getCustomQuestion(q: LeadformQuestion): ?LeadformCustomQuestion {
  switch (q.type) {
    case 'custom':
      return { type: 'input', title: q.value }
    case 'text':
      return { type: 'textarea', title: q.value }
    case 'radio':
      return { type: 'radio', title: q.value, options: q.options }
    case 'checkbox':
      return { type: 'checkbox', title: q.value, options: q.options }
    case 'select':
      return { type: 'select', title: q.value, options: q.options }
    default:
      return null
  }
}

function formatQuestions(
  stockQuestions: LeadformStockQuestions,
  customQuestions: Array<LeadformCustomQuestion>
): Array<LeadformQuestion> {
  const result = []
  if (stockQuestions.name) result.push({ type: 'first_name' })
  if (stockQuestions.patronymic) result.push({ type: 'middle_name' })
  if (stockQuestions.surname) result.push({ type: 'last_name' })
  if (stockQuestions.email) result.push({ type: 'email' })
  if (stockQuestions.phone) result.push({ type: 'phone_number' })
  if (stockQuestions.age) result.push({ type: 'age' })
  if (stockQuestions.birthday) result.push({ type: 'birthday' })
  if (stockQuestions.location) result.push({ type: 'location' })
  return [...result, ..._.map(customQuestions, (q) => formatCustomQuestion(q))]
}

function formatCustomQuestion(q: LeadformCustomQuestion): LeadformQuestion {
  if (q.type === 'input') {
    return { type: 'custom', value: q.title }
  } else if (q.type === 'textarea') {
    return { type: 'text', value: q.title }
  } else if (q.type === 'radio') {
    return { type: 'radio', value: q.title, options: q.options || [] }
  } else if (q.type === 'checkbox') {
    return { type: 'checkbox', value: q.title, options: q.options || [] }
  } else if (q.type === 'select') {
    return { type: 'select', value: q.title, options: q.options || [] }
  } else {
    return { type: 'custom', value: `Unknown type ${q.type}` }
  }
}

function processErrors(errors): LeadformErrors {
  return {
    nameErrors: errors.name || [],
    vkGroupErrors: errors.vk_public_id || [],
    fbGroupErrors: errors.fb_public_id || [],
    titleErrors: errors.title || [],
    descriptionErrors: errors.description || [],
    imageErrors: errors.image_id || [],
    questionsErrors: errors.questions || [],
    privacyPolicyUrlErrors: errors.privacy_policy_url || [],
    confirmationTextErrors: errors.confirmation_text || []
  }
}

const FormContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(Form)

export default FormContainer
